import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { FAQ_QUESTION_PHONE_BINDING, getHelpFaqQuestionRoute } from '@/app/routes';
import { ForeignNumberNotice } from '@/components/common/phone/ForeignNumberNotice';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { Theme } from '@mui/material/styles';
import { TypographyLink } from '@/components/common/TypographyLink';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import React, { FC, Fragment, ReactElement } from 'react';

/**
 * Id of the barrier's heading. It stays in the body so the lock icon can sit above it,
 * which is why the dialog points `aria-labelledby` here
 * instead of letting MUI name itself from a title row that holds only the ✕.
 */
export const PHONE_BINDING_NOTICE_TITLE_ID = 'phone-binding-notice-title';

interface Props {
  isBarrier: boolean;
  isMobile?: boolean;
  onBind: () => void;
}

export const PhoneBindingNotice: FC<Props> = (props: Props): ReactElement => {
  const { isBarrier, isMobile, onBind } = props;
  const navigate: NavigateFunction = useNavigate();

  const handleFaqClick = (): void => {
    navigate(getHelpFaqQuestionRoute(FAQ_QUESTION_PHONE_BINDING));
  };

  return (
    <Grid
      container={true}
      direction={'column'}
      rowSpacing={2}
      data-testid={isBarrier ? 'phone-binding-barrier' : 'phone-binding-reminder'}>
      {isBarrier && (
        <Fragment>
          <Grid item={true}>
            <Box
              display={'flex'}
              alignItems={'center'}
              justifyContent={'center'}
              width={'56px'}
              height={'56px'}
              mx={'auto'}
              borderRadius={'50%'}
              sx={{ backgroundColor: (theme: Theme) => theme.colors.lightBlueBackground }}>
              <LockOutlinedIcon sx={{ width: '28px', height: '28px', color: (theme: Theme) => theme.colors.primary }} />
            </Box>
          </Grid>
          <Grid item={true}>
            {/* The escape is a non-breaking space: it keeps the last word off a line of its own. */}
            <Typography id={PHONE_BINDING_NOTICE_TITLE_ID} variant={'h1'}>
              {'Привяжите номер телефона, чтобы\u00A0продолжить'}
            </Typography>
          </Grid>
        </Fragment>
      )}
      <Grid item={true}>
        {isBarrier ? (
          <Typography variant={'body1'}>
            {'Доступ в личный кабинет закрыт, пока не привязан и не подтверждён номер телефона, согласно ФЗ-149.'}
          </Typography>
        ) : (
          <Typography variant={'body1'}>
            {'С '}
            <Typography variant={'inherit'} component={'span'} fontWeight={600}>
              {'1 сентября 2026 года '}
            </Typography>
            {
              'вход в личный кабинет будет доступен только пользователям с привязанным номером мобильного телефона, согласно ФЗ-149.'
            }
          </Typography>
        )}
      </Grid>
      <Grid item={true}>
        <ForeignNumberNotice />
      </Grid>
      {!isMobile && (
        <Grid item={true}>
          <Button fullWidth={true} variant={'contained'} onClick={onBind} data-testid={'bind-phone'}>
            {'Привязать номер'}
          </Button>
        </Grid>
      )}
      <Grid item={true}>
        <Divider />
      </Grid>
      <Grid item={true}>
        <Typography variant={'body2'} align={'center'}>
          <TypographyLink onClick={handleFaqClick} data-testid={'phone-binding-faq'}>
            {'Подробнее про привязку номера телефона'}
          </TypographyLink>
        </Typography>
      </Grid>
    </Grid>
  );
};
